import type {
  CreateQuestionPayload,
  GetQuestionsPayload,
  Question,
  QuestionListItem,
  ReorderQuestionsPayload,
  UpdateQuestionPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type QuestionsResponse = { questions: QuestionListItem[] };
type QuestionResponse = { question: Question };

const questionListTags = (result: QuestionListItem[] | undefined) =>
  result
    ? [...result.map(({ id }) => ({ type: 'Question' as const, id })), { type: 'Question' as const, id: 'LIST' }]
    : [{ type: 'Question' as const, id: 'LIST' }];

/**
 * Mirrors `apps/server/src/modules/question` (`questionController`, prefix `/questions`).
 * `create`/`delete` invalidate `Module` `LIST` too since `Module`/`ModuleListItem.questions_count`
 * is counted live (see `module.api.ts`). `update` only moves a question between an existing
 * category/scale or edits its text — it never changes any count, so no `Module` invalidation
 * there. `reorderQuestions` (`PUT /questions/reorder`) is a batch `order_index` update across an
 * arbitrary set of questions — invalidating the whole `Question` `LIST` is simpler and no more
 * expensive than resolving exactly which ids moved.
 */
export const questionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getQuestions: builder.query<QuestionListItem[], GetQuestionsPayload>({
      query: (payload) => ({ path: ['questions'], method: 'get', query: payload }),
      transformResponse: (response: QuestionsResponse) => response.questions,
      providesTags: (result) => questionListTags(result),
    }),

    getQuestionById: builder.query<Question, string>({
      query: (id) => ({ path: ['questions', { param: 'question_id', value: id }], method: 'get' }),
      transformResponse: (response: QuestionResponse) => response.question,
      providesTags: (_result, _error, id) => [{ type: 'Question', id }],
    }),

    createQuestion: builder.mutation<Question, CreateQuestionPayload>({
      query: (body) => ({ path: ['questions'], method: 'post', body }),
      transformResponse: (response: QuestionResponse) => response.question,
      invalidatesTags: [
        { type: 'Question', id: 'LIST' },
        { type: 'Module', id: 'LIST' },
      ],
    }),

    updateQuestion: builder.mutation<Question, { id: string; payload: UpdateQuestionPayload }>({
      query: ({ id, payload }) => ({
        path: ['questions', { param: 'question_id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: QuestionResponse) => response.question,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Question', id },
        { type: 'Question', id: 'LIST' },
      ],
    }),

    reorderQuestions: builder.mutation<void, ReorderQuestionsPayload>({
      query: (body) => ({ path: ['questions', 'reorder'], method: 'put', body }),
      transformResponse: (): void => undefined,
      invalidatesTags: [{ type: 'Question', id: 'LIST' }],
    }),

    deleteQuestion: builder.mutation<void, string>({
      query: (id) => ({ path: ['questions', { param: 'question_id', value: id }], method: 'delete' }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, id) => [
        { type: 'Question', id },
        { type: 'Question', id: 'LIST' },
        { type: 'Module', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetQuestionsQuery,
  useGetQuestionByIdQuery,
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
  useReorderQuestionsMutation,
  useDeleteQuestionMutation,
} = questionApi;
